import { useFetch } from '@/lib/fin/use-fetch';
import { useCurrency } from '@/context/CurrencyContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArrowLeft, Building2, MapPin, Shield, FileCheck, GitBranch, Landmark, Wallet,
} from 'lucide-react';

interface EntityDetailProps {
  entityId: string;
  // Accepts the same (page, params?) signature as Dashboard.finNavigate so
  // we can route back to the entity list and other fin pages correctly.
  onNavigate?: (page: string, params?: Record<string, string>) => void;
}

// Mirrors the GET /api/fin/entities response shape exactly — do not add
// fields the API doesn't return or the display will silently render
// `undefined` (which is what happened before this fix on `code`,
// `licence_ref`, `incorporation_date`, `notes`).
interface EntityData {
  id: string;
  name: string;
  legal_name: string | null;
  jurisdiction: string | null;
  regulator: string | null;
  licence_type: string | null;
  parent_entity: string | null;
  parent_name: string | null;
  group_tag: string | null;
  is_active: boolean;
  min_capital_eur: number | string | null;
}

interface BankAccountSummary {
  id: string;
  entity_id: string;
  bank_name: string;
  iban: string | null;
  currency: string;
  account_type: string | null;
  is_active: boolean;
}

const GROUP_LABELS: Record<string, string> = {
  wwg: 'WWG Group',
  vault: 'The Vault Group',
  tff: 'Together Forever Foundation',
};
function groupLabel(tag: string | null): string {
  if (!tag) return '—';
  return GROUP_LABELS[tag] || tag;
}

export default function EntityDetail({ entityId, onNavigate }: EntityDetailProps) {
  const { fmt: cfmt } = useCurrency();
  const fmtEur = (v: number | string | null): string => {
    if (v == null || v === '') return '—';
    const n = Number(v);
    if (!isFinite(n)) return '—';
    return cfmt(n, 'EUR');
  };
  const { data: entities, loading } = useFetch<EntityData[]>('/api/fin/entities');
  const { data: accounts } = useFetch<BankAccountSummary[]>('/api/fin/bank-accounts');
  const entity = (entities || []).find(e => e.id === entityId);

  // Correct FinPage values — Dashboard.tsx routes on 'fin-entities' and
  // 'fin-accounts', not 'entities' / 'registry'. Passing an unknown value
  // leaves finPage in a state where no conditional matches and the
  // content area renders nothing (what the user saw as a blank crash).
  const goBack = () => onNavigate?.('fin-entities');
  const goAccounts = () => onNavigate?.('fin-accounts');

  if (loading) {
    return (
      <div className="p-4 md:p-6 space-y-6">
        <Skeleton className="h-7 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!entity) {
    return (
      <div className="p-4 md:p-6 space-y-6">
        <Button variant="ghost" className="gap-2" onClick={goBack}>
          <ArrowLeft size={16} /> Back to Entities
        </Button>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <Building2 size={32} className="text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">Entity not found</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const entityAccounts = (accounts || []).filter(a => a.entity_id === entity.id);
  const activeAccounts = entityAccounts.filter(a => a.is_active);

  return (
    <div className="p-4 md:p-6 space-y-6">
      <Button variant="ghost" className="gap-2" onClick={goBack}>
        <ArrowLeft size={16} /> Back to Entities
      </Button>

      <div className="flex items-center gap-4">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-foreground tracking-tight">{entity.name}</h1>
            <Badge variant={entity.is_active ? 'default' : 'secondary'}>
              {entity.is_active ? 'Active' : 'Inactive'}
            </Badge>
          </div>
          {entity.legal_name && entity.legal_name !== entity.name && (
            <p className="text-sm text-muted-foreground mt-1">{entity.legal_name}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Basic Info */}
        <Card className="shadow-xs">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Entity Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <InfoRow icon={<Building2 size={14} />} label="Legal Name" value={entity.legal_name} />
            <InfoRow icon={<MapPin size={14} />} label="Jurisdiction" value={entity.jurisdiction} />
            <InfoRow icon={<Shield size={14} />} label="Regulator" value={entity.regulator} />
            <InfoRow icon={<FileCheck size={14} />} label="Licence Type" value={entity.licence_type} />
            <InfoRow icon={<GitBranch size={14} />} label="Parent" value={entity.parent_name} />
            <InfoRow icon={<Building2 size={14} />} label="Group" value={groupLabel(entity.group_tag)} />
            <InfoRow icon={<Wallet size={14} />} label="Min Capital" value={fmtEur(entity.min_capital_eur)} />
          </CardContent>
        </Card>

        {/* Associated bank accounts */}
        <Card className="shadow-xs">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Landmark size={14} /> Bank Accounts
              <Badge variant="outline" className="text-[10px]">
                {activeAccounts.length} active / {entityAccounts.length} total
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {entityAccounts.length === 0 ? (
              <p className="text-sm text-muted-foreground">No bank accounts recorded for this entity.</p>
            ) : (
              <ul className="space-y-1.5 text-xs">
                {entityAccounts.slice(0, 10).map(a => (
                  <li key={a.id} className={`flex items-center justify-between gap-2 ${!a.is_active ? 'opacity-50' : ''}`}>
                    <div className="truncate">
                      <span className="font-medium">{a.bank_name}</span>
                      {a.iban && <span className="font-mono text-muted-foreground ml-2">{a.iban}</span>}
                    </div>
                    <Badge variant="secondary" className="text-[10px] shrink-0">{a.currency}</Badge>
                  </li>
                ))}
                {entityAccounts.length > 10 && (
                  <li className="text-muted-foreground italic">
                    … and {entityAccounts.length - 10} more. Click "View all accounts" below.
                  </li>
                )}
              </ul>
            )}
            <Button variant="outline" size="sm" className="mt-3" onClick={goAccounts}>
              View all accounts
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function InfoRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | null | undefined }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <div className="flex items-center gap-2 text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <span className="text-foreground font-medium">{value || '—'}</span>
    </div>
  );
}
